<template>
    <div id="contact-list" class="container-fluid main-content page-content">
        <div class="page-header contacts-header">
            <div class="contacts-title">
                <h3 class="page-title">{{ $t('all-contacts') }}</h3>
                <button
                    type="button"
                    class="btn text-btn shadow-none contacts-aside-toggle"
                    :aria-expanded="showSidebar ? 'true' : 'false'"
                    aria-controls="contact-list-sidebar"
                    @click="showSidebar = !showSidebar">
                    <template v-if="showSidebar">
                        {{ $t('close') }} <font-awesome-icon class="text-primary" :icon="['fas', 'angle-double-right']" />
                    </template>
                    <font-awesome-icon class="text-primary" :icon="['fas', 'angle-double-left']" v-else />
                </button>
            </div>
            <div class="row mb-2">
                <div class="col-sm text-left align-self-center">
                    <div class="position-relative">
                        <input
                            type="text"
                            class="form-control fs-13 py-1 pr-4"
                            v-bind:placeholder="$t('search-contact')"
                            @input="activateFilter"
                            v-model="search"
                        >

                        <span
                            v-if="search"
                            class="clear-search"
                            @click="clearSearch"
                        >
                            &times;
                        </span>
                    </div>
                </div>
                <div class="col-xl col-sm text-left">
                    <form>
                        <select-dropdown
                            :options="options"
                            :pageName="'contact-list'"
                            :selected="selectedOption"
                            v-on:selectOption="updateOption($event)"
                            :type="'options'"
                            :showLabel="true">
                        </select-dropdown>
                    </form>
                </div>
                <div class="col-sm-auto align-self-center">
                    <button type="button" class="btn text-primary rounded-pill shadow-none fs-14 font-nunito-SemiBold" @click="confirmExportContacts()">
                        <font-awesome-icon class="text-primary" :icon="['fas', 'upload']" />
                        {{ $t('Export-contact') }}
                    </button>
                </div>
            </div>
            <advance-search></advance-search>
            <contact-list-tags
                ref="contactsTag"
                v-bind:collapseSidebar="showSidebar"
                v-bind:global-loading="loading"
                v-bind:contacts="contacts">
            </contact-list-tags>
        </div>
        <div class="contacts-body" v-bind:class="{ 'sidebar-hidden': !showSidebar }">
            <div class="contacts-main">
                <preloader-placeholder :loading="loading" :resultlength="Object.keys(this.contacts).length" :type="'contact'"></preloader-placeholder>
                <div class="box-wrap white-scrollbar pr-3" v-if="loading === false">
                    <all-contact-box
                        v-for="contact in contacts"
                        v-bind:key="contact.id"
                        v-bind:contact="contact"
                    ></all-contact-box>
                </div>
                <b-pagination
                    v-model="page.number"
                    :total-rows="contactsMeta.page.total"
                    :per-page="contactsMeta.page['per-page']"
                    @input="getContactsPage"
                    align="center"
                ></b-pagination>
            </div>
            <contactlistsidebar></contactlistsidebar>
        </div>
    </div>
</template>
<script>

// Components
import AllContactBox        from './AllContactbox.vue';
import SelectDropdown       from '../../../core/components/SelectDropdown.vue';
import PreloaderPlaceholder from '../PreloaderPlaceholder.vue';
import AdvanceSearch        from './ContactAdvanceSearch.vue';
import ContactListTags      from './ContactListTags.vue';
import ContactlistSidebar   from './ContactlistSidebar.vue';

// Internal services js
import { EventBus } from '../../../helpers/EventBus.js';
import Alert from '@/helpers/Alert.js';
import { escapeHtml } from '@/plugins/dialog';

// Import external libraries
import { mapState, mapActions } from 'vuex';
import moment                   from 'moment';

export default {
    components: {
        AllContactBox,
        SelectDropdown,
        PreloaderPlaceholder,
        AdvanceSearch,
        ContactListTags,
        'contactlistsidebar': ContactlistSidebar
    },

    data () {
        return {
            showSidebar:       true,
            withTagContact:    [],
            withoutTagContact: [],
            contactName:       '',
            contactImage:      '',
            contactTags:       [],
            contactId:         '',
            createdDate:       '',
            search:            '',
            timer:             null,
            loading:           false,
            selectedFilter:    '',
            startDateSearch:   '',
            endDateSearch:     '',
            selectedOption:    this.$i18n.t('all'),
            page:              {
                number: 1,
                size:   25
            },
            options: [
                'all',
                'sms-unsubscribed',
                'new-contact-7days',
                'archived'
            ]
        };
    },

    computed: {
        ...mapState('core/contacts', [
            'contacts',
            'contactsMeta'
        ]),

        ...mapState('core/auth', [
            'user'
        ])
    },

    mounted () {
        // Get first contacts page
        this.getContactsPage();

        // Update the contact list after importing contacts
        EventBus.$off('contact-imported');
        EventBus.$on('contact-imported', () => {
            this.page.number = 1;
            this.getContactsPage();
        });

        // Place tag in array to search contact with does tags.
        EventBus.$off('with-tag-search');
        EventBus.$on('with-tag-search', (withTag) => {
            this.withTagContact = withTag;
            this.activateFilter();
        });

        // Place tag in array to search contact without does tags.
        EventBus.$off('without-tag-search');
        EventBus.$on('without-tag-search', (withoutTag) => {
            this.withoutTagContact = withoutTag;
            this.activateFilter();
        });

        // Set date range starting date, if date is null turn in empty string
        EventBus.$off('startDateSearch');
        EventBus.$on('startDateSearch', (startDate) => {
            this.startDateSearch = startDate === null ? '' : startDate;
            this.activateFilter();
        });

        // Set date range ending date, if date is null turn in empty string
        EventBus.$off('endDateSearch');
        EventBus.$on('endDateSearch', (endDate) => {
            this.endDateSearch = endDate === null ? '' : endDate;
            this.activateFilter();
        });
    },

    methods: {
        /**
         * Define moment to use it in template
         */
        moment,
        ...mapActions('core/contacts', [
            'index',
            'export'
        ]),

        /**
         * Update selected option
         *
         * @return    void
         */
        updateOption (option) {
            this.selectedOption = this.$i18n.t(option);
            this.selectedFilter = option;

            // Cancel the selected contacts
            this.$refs.contactsTag.cancelSelect();

            this.activateFilter();
        },

        /**
         * Confirm then export the contacts.
         *
         * @return {void}
         */
        confirmExportContacts () {
            const email = escapeHtml(this.user.attributes.email);

            this.$dialog.confirm({
                title:       this.$t('confirmation-required'),
                html:        this.$t('confirmation-export-contacts', { email }),
                confirmText: this.$t('export')
            })
                .then((confirmed) => {
                    if (confirmed) {
                        this.exportContacts();
                    }
                });
        },

        /**
         * Get and download the liste of contacts in excel file.
         *
         * @return    void
         */
        exportContacts () {
            this.export({ filters: this.getFiltersParameter() })
                .then(() => {
                    Alert.success(`${this.$i18n.t('file-send')} ${this.user.attributes.email}.`);
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('mail-faild'));
                });
        },

        /**
         * Get the filters
         *
         * @retun   {object}    The filters parameter
         */
        getFiltersParameter () {
            const nbDaysWeek = 7;
            let fromDate = '';

            // Check if from filter is from filter or advance search
            if (this.startDateSearch !== '') {
                fromDate = this.startDateSearch;
            } else if (this.selectedFilter === 'new-contact-7days' && this.startDateSearch === '') {
                fromDate = moment().utc()
                    .subtract(nbDaysWeek, 'days')
                    .format('YYYY-MM-DD');
            }

            return {
                'name|email|phone-number-custom-filter|first-name|last-name': this.search === '' ? void 0 : this.search,
                'only-trashed':                                               this.selectedFilter === 'archived' ? true : void 0,
                'with-tags':                                                  this.withTagContact.join() === '' ? void 0 : this.withTagContact.join(),
                'without-tags':                                               this.withoutTagContact.join() === '' ? void 0 : this.withoutTagContact.join(),
                'is-message-allowed':                                         this.selectedFilter === 'sms-unsubscribed' ? 'not' : void 0,
                from:                                                         fromDate === '' ?  void 0 : moment(fromDate).utc()
                    .format('YYYY-MM-DD'),
                to: this.endDateSearch === '' ? void 0 : moment(this.endDateSearch).utc()
                    .format('YYYY-MM-DD')
            };
        },

        /**
         * Activate search in contact list by typing in search bar
         * Set a 500ms debounce to give time to user to type before making a ajax call
         *
         * @param     {string}    value of search bar in this.search
         *
         * @return    {object}    list of contacts that match the search
         */
        activateFilter () {
            // Cancel the selected contacts
            this.$refs.contactsTag.cancelSelect();

            // Change contact list with filters
            const searchDelay = 500;
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.getContactsPage();
            }, searchDelay);
        },

        /**
         * Clear the search bar and reload the full contact list right away
         *
         * @return    void
         */
        clearSearch () {
            this.search = '';
            this.$refs.contactsTag.cancelSelect();
            clearTimeout(this.timer);
            this.getContactsPage();
        },

        /**
         * Get list of contacts by page
         *
         * @return    void
         */
        getContactsPage () {
            // Start loading from the api.
            this.loading = true;

            // Get list of contacts
            this.index({
                filters: this.getFiltersParameter(),
                page:    this.page
            }).then(() => {
                // Finish loading
                this.loading = false;
            });
        }
    }
};
</script>
